import { ref, watch, computed, onScopeDispose, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { Dark } from "quasar";
import type { Contact } from "@/common/model/crm/contact";
import { CommonHelper } from "@/common/helper/common-helper";
import { ColorHelper } from "@/common/helper/color-helper";
import CustomEventEmitter from "@/common/helper/event-emitter";
import contactService from "@/common/services/contact-service";
import { authService } from "./common/services/auth-service";
import { ticketEntity } from "@/common/model/entity/ticket-entity";
import { LocalStorageService } from "@/common/helper/local-storage-service";
import { Enums } from "@/common/helper/enums";
import {
  CX_APP_SETTINGS_DEFAULTS,
  DEFAULT_LOGO_DARK,
  DEFAULT_LOGO_LIGHT,
  getCxAppLogoUrl,
  resolveAccessibleLogoUrl,
  resolveSignInBackgroundTheme,
} from "@/common/model/cx-app-settings";
import { CxAppSettingsService } from "@/common/services/cx-app-settings-service";
import { projectEntity } from "@/common/model/project/project-entity";
import { projectSidebarListRevision } from "@/project/project-sidebar-list-refresh";

/** Portal theme ids: 1 = dark, 2 = light (matches legacy App.vue / preset-loader). */
const DARK_THEME_ID = 1;
const LIGHT_THEME_ID = 2;

type ContactWithTheme = Contact & { themeId?: number };
type ThemeTextColors = {
  textColorLight: string;
  textColorDark: string;
};

function isDarkThemeId(themeId: number | undefined | null): boolean {
  return themeId !== LIGHT_THEME_ID;
}

function themeIdFromDark(dark: boolean): number {
  return dark ? DARK_THEME_ID : LIGHT_THEME_ID;
}

function isDarkFromSignInSettings(): boolean {
  const settings = LocalStorageService.getCxAppSettingsFromLocalStorage();
  if (!settings) {
    return isDarkThemeId(undefined);
  }
  return (
    resolveSignInBackgroundTheme(settings) !== Enums.SignInBackgroundTheme.Light
  );
}

export function useApp() {
  const store = useStore();
  const contactThemeId = () => {
    const contact = store.getters[
      "ContactModule/getContact"
    ] as ContactWithTheme | null;
    return contact?.themeId;
  };

  const isDark = ref(
    contactThemeId() != null
      ? isDarkThemeId(contactThemeId())
      : isDarkFromSignInSettings(),
  );
  const isSidebarOpen = ref(true);
  const route = useRoute();
  const router = useRouter();

  const hideNavigation = computed(() => route.meta.hideNavigation);

  /** Documents list and detail share one route; router-link active-class alone does not cover both paths. */
  const isDocumentsNavActive = computed(() => {
    const { name, path } = route;
    if (name === "DocumentManage" || name === "DocumentManageComponent") {
      return true;
    }
    return path === "/documentManage" || path.startsWith("/documentManage/");
  });

  const brandColors = computed(() => {
    void (store.getters["MasterModule/resetTheme"] as number);
    const settings = LocalStorageService.getCxAppSettingsFromLocalStorage();
    const brandColorLightHex =
      settings?.brandColorLight ?? CX_APP_SETTINGS_DEFAULTS.brandColorLight;
    const brandColorDarkHex =
      settings?.brandColorDark ?? CX_APP_SETTINGS_DEFAULTS.brandColorDark;
    return {
      brandColorLight: ColorHelper.toCssHsl(brandColorLightHex),
      brandColorDark: ColorHelper.toCssHsl(brandColorDarkHex),
    };
  });

  /** For `v-bind()` in App.vue `<style>` (and template). */
  const brandColorLight = computed(() => brandColors.value.brandColorLight);
  const brandColorDark = computed(() => brandColors.value.brandColorDark);

  const themeSettings = computed((): ThemeTextColors => {
    void (store.getters["MasterModule/resetTheme"] as number);
    const settings = LocalStorageService.getCxAppSettingsFromLocalStorage();
    const textColorLightHex =
      settings?.themeSettings.light.textColor ??
      CX_APP_SETTINGS_DEFAULTS.themeSettings.light.textColor;
    const textColorDarkHex =
      settings?.themeSettings.dark.textColor ??
      CX_APP_SETTINGS_DEFAULTS.themeSettings.dark.textColor;

    return {
      textColorLight: textColorLightHex,
      textColorDark: textColorDarkHex,
    };
  });

  const textColorLight = computed(() => themeSettings.value.textColorLight);
  const textColorDark = computed(() => themeSettings.value.textColorDark);

  const canSeeAssetSummary = computed(
    () => store.getters["ContactModule/getHasAllAssetsTag"] as boolean,
  );

  const canSeeInvoices = computed(
    () => store.getters["ContactModule/getHasAllInvoicesTag"] as boolean,
  );

  const canSeeTicketSummary = computed(
    () => store.getters["ContactModule/getHasAllTicketsTag"] as boolean,
  );

  const canSeeReports = computed(
    () => canSeeAssetSummary.value || canSeeTicketSummary.value,
  );

  const canSeeProjects = computed(() => {
    void projectSidebarListRevision.value;
    return projectEntity.count() > 0;
  });

  watch(canSeeProjects, (visible) => {
    if (!visible && route.name === "ProjectList") {
      store.commit("MasterModule/SET_SELECTED_PROJECT_ID", "");
      void router.replace({ name: "TicketCreateComponent" });
    }
  });

  const contact = computed(
    () => store.getters["ContactModule/getContact"] as Contact | null,
  );

  /** Portal sidebar/mobile logo only — does not use the shared store (login/magic-link use store). */
  const portalAppIconLink = ref("");

  async function refreshPortalAppIcon() {
    const settings = LocalStorageService.getCxAppSettingsFromLocalStorage();
    if (!settings) {
      return;
    }
    const themeId = themeIdFromDark(isDark.value);
    const themedLogo = getCxAppLogoUrl(settings, themeId);
    const resolved = resolveAccessibleLogoUrl(themedLogo, settings.appLogoUrl);
    let logo =
      resolved && resolved !== ""
        ? resolved
        : themeId === LIGHT_THEME_ID
        ? DEFAULT_LOGO_LIGHT
        : DEFAULT_LOGO_DARK;
    logo = await CxAppSettingsService.ensureLogoUrlAccessible(logo);
    portalAppIconLink.value = logo;
  }

  const getAppIconLink = computed(() => {
    if (portalAppIconLink.value) {
      return portalAppIconLink.value;
    }
    return store.getters["MasterModule/appIconLink"] as string;
  });
  const getOrganizationName = computed(
    () => store.getters.getServiceProviderName as string,
  );

  const userDisplayName = computed(() => {
    const name = (
      store.getters["ContactModule/getContactName"] as string
    )?.trim();
    return name.length > 0 ? name : "—";
  });

  const userInitials = computed(() => {
    const c = contact.value;
    if (!c) {
      return "—";
    }
    const initials = CommonHelper.getInitials(
      c.firstName ?? "",
      c.lastName ?? "",
    );
    return initials === "N/A" ? "—" : initials;
  });

  const userEmail = computed(() => {
    const email = contact.value?.emailAddress?.trim();
    return email && email.length > 0 ? email : "—";
  });

  const unreadMessagesCount = computed(
    () =>
      ticketEntity
        .query()
        .all()
        .filter((ticket) => ticket.isUnread === true).length,
  );

  const unreadMessagesBadgeLabel = computed(() => {
    const count = unreadMessagesCount.value;
    if (count <= 0) {
      return "";
    }
    return count > 99 ? "99+" : String(count);
  });

  function applyBrandColors() {
    const root = document.documentElement;
    root.style.setProperty("--color-brand-05-dark", brandColorDark.value);
    root.style.setProperty("--color-brand-05-light", brandColorLight.value);
    root.style.setProperty(
      "--color-buttons-primary-surface-dark",
      brandColorDark.value,
    );
    root.style.setProperty(
      "--color-buttons-primary-surface-light",
      brandColorLight.value,
    );
    root.style.setProperty(
      "--color-buttons-primary-surface-hover-dark",
      ColorHelper.adjustCssHslLightness(brandColorDark.value, -5),
    );
    root.style.setProperty(
      "--color-buttons-primary-surface-hover-light",
      ColorHelper.adjustCssHslLightness(brandColorLight.value, -5),
    );
  }
  function applyThemeTextColors() {
    const root = document.documentElement;
    const activeTextColor = isDark.value
      ? textColorDark.value
      : textColorLight.value;
    root.style.setProperty("--theme-text-color", activeTextColor);
    root.style.setProperty("--color-login-text-color", activeTextColor);
  }

  function applyTheme(dark?: boolean) {
    const resolved = dark ?? isDarkFromSignInSettings();
    isDark.value = resolved;
    Dark.set(resolved);
    applyThemeTextColors();
    if (!hideNavigation.value && contactThemeId() != null) {
      void refreshPortalAppIcon();
    }
  }

  function applySignInBrandingFromStorage() {
    applyBrandColors();
    applyThemeTextColors();
    if (contactThemeId() == null) {
      applyTheme(isDarkFromSignInSettings());
    } else if (!hideNavigation.value) {
      void refreshPortalAppIcon();
    }
  }

  async function setTheme(dark: boolean) {
    applyTheme(dark);

    if (hideNavigation.value) {
      return;
    }

    const contact = store.getters[
      "ContactModule/getContact"
    ] as ContactWithTheme | null;
    const contactId = store.getters["ContactModule/getContactId"] as number;
    if (!contact || !contactId) {
      return;
    }

    const themeId = themeIdFromDark(dark);
    if (contact.themeId === themeId) {
      return;
    }

    try {
      const updated = (await contactService.setTheme({
        contactId,
        themeId,
      })) as ContactWithTheme;
      const contactToStore = { ...contact, ...updated, themeId };
      contactService.AddContactToStore(contactToStore);
    } catch (e) {
      console.error(e);
    }
  }

  const signOutLoading = ref(false);

  async function signOut() {
    signOutLoading.value = true;
    try {
      store.commit("ContactModule/RESET_STATE");
      await authService.logout();
    } catch (e) {
      console.error(e);
    } finally {
      signOutLoading.value = false;
    }
  }
  watch([textColorDark, textColorLight], applyThemeTextColors);
  watch([brandColorDark, brandColorLight], applyBrandColors);

  const stopThemeClassWatch = watch(
    isDark,
    (val: boolean) => {
      document.body.classList.toggle("theme-light", !val);
      document.body.classList.toggle("theme-dark", val);
    },
    { immediate: true },
  );

  const stopResetThemeWatch = watch(
    () => store.getters["MasterModule/resetTheme"] as number,
    applySignInBrandingFromStorage,
  );

  const onBootApp = (themeId: number) => {
    if (themeId) {
      applyTheme(isDarkThemeId(themeId));
    }
  };
  CustomEventEmitter.getInstance().on("bootApp", onBootApp);

  onMounted(() => {
    const themeId = contactThemeId();
    if (themeId != null) {
      applyTheme(isDarkThemeId(themeId));
    } else {
      applyTheme();
    }
    applyBrandColors();
    applyThemeTextColors();
  });

  onScopeDispose(() => {
    stopThemeClassWatch();
    stopResetThemeWatch();
    CustomEventEmitter.getInstance().off("bootApp", onBootApp);
  });
  return {
    isDark,
    isSidebarOpen,
    brandColors,
    brandColorLight,
    brandColorDark,
    themeSettings,
    textColorLight,
    textColorDark,
    hideNavigation,
    canSeeAssetSummary,
    canSeeInvoices,
    canSeeProjects,
    canSeeReports,
    canSeeTicketSummary,
    userDisplayName,
    userInitials,
    userEmail,
    setTheme,
    signOut,
    signOutLoading,
    unreadMessagesCount,
    unreadMessagesBadgeLabel,
    getAppIconLink,
    getOrganizationName,
    isDocumentsNavActive,
  };
}
